import { FC, ReactNode, useCallback } from 'react';

import { Avatar as AntAvatar, Tooltip } from 'antd';
import { AvatarSize } from 'antd/es/avatar/AvatarContext';

import { resolveMainApiHost } from 'utils/api/api';

import { Attachment } from 'types/commons';

import { RESOURCE_PUBLIC } from 'constants/endpoints';
import { UserAttachmentTypes } from 'constants/enums';

interface AvatarProps {
  name?: string;
  attachments?: Attachment<UserAttachmentTypes>[];
  children?: ReactNode;
  color?: string;
  tooltip?: string;
  onClick?: () => void;
  size?: AvatarSize;
}

const Avatar: FC<AvatarProps> = ({
  name,
  attachments,
  children,
  color,
  tooltip,
  onClick,
  size = 'large'
}) => {
  const getAvatarColor = (name?: string): string => {
    const value = name
      ? name
          .split(/\s/)
          .reduce((response, word) => (response += word.slice(0, 1)), '')
      : '';
    let hash = 0;
    for (let i = 0; i < value.length; i++) {
      hash = value.charCodeAt(i) + ((hash << 5) - hash);
    }

    return `hsl(${hash % 360}, 85%, 35%)`;
  };

  const getSource = (attachments?: Attachment<UserAttachmentTypes>[]) => {
    const mainHost = `${resolveMainApiHost()}${RESOURCE_PUBLIC}`;

    if (!attachments || attachments.length === 0) {
      return null;
    }

    const avatarAttachment = attachments.filter(
      (a) => a.type === UserAttachmentTypes.UserAvatar
    )[0];

    if (!avatarAttachment) {
      return null;
    }

    const thumbnail = avatarAttachment.thumbnails[0];

    return thumbnail
      ? `${mainHost}${avatarAttachment.id}/thumb/${thumbnail.url}`
      : `${mainHost}${avatarAttachment.id}`;
  };

  const getNameLetters = (name?: string): string => {
    return name
      ? name
          .split(/\s/)
          .reduce((response, word) => (response += word.slice(0, 1)), '')
      : '';
  };

  const handleClick = useCallback(() => {
    if (onClick) {
      onClick();
    }
  }, [onClick]);

  const avatarColor = color || getAvatarColor(name);
  const source = getSource(attachments);
  const nameLetters = getNameLetters(name);

  return (
    <Tooltip title={tooltip} placement="top" trigger="hover">
      <AntAvatar
        size={size}
        style={{
          backgroundColor: avatarColor,
          cursor: onClick ? 'pointer' : 'default'
        }}
        src={source}
        onClick={handleClick}
      >
        {nameLetters}
        {children && children}
      </AntAvatar>
    </Tooltip>
  );
};

export default Avatar;
